import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Modal, InputNumber, Select, Input, message, Button, AutoComplete } from '../../ui';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { Pill } from '../../components/TwDataTable';

/**
 * WHM RECEIVE — Phase 2 of the return lifecycle. The warehouse manager inspects the goods that came back and
 * marks the return RECEIVED. A returned product line can split across several SUB-LOTS — different batches
 * AND/OR conditions (e.g. of 5 units of lot A, 2 damaged + 3 expired) — so each line carries one or more
 * allocation rows: qty + batch (pre-filled with the dispatched lot; a mismatch is flagged) + condition. Saleable
 * (good / near-expiry) rows re-enter that saleable lot; damaged / mismatch / expired / hold are quarantined.
 * Short receipts are allowed. Money (the credit note) is the later Accounts step.
 *
 * Styled in the house Tailwind language (theme.css tokens → light/dark correct): AntD keeps the (brand-themed)
 * modal shell + form controls, everything else is Tailwind cards / TH-style headers / Pill chips.
 */

// Agri-goods condition → stock disposition (mirrors the backend CONDITION_DISPOSITION). Saleable conditions
// re-enter AVAILABLE stock (the received lot); the rest are quarantined so nothing unfit is re-sold to a farmer.
const CONDITION_OPTIONS = [
  { value: 'RESALABLE',   label: 'Resalable — good',   hint: 'saleable',   pill: 'green' },
  { value: 'NEAR_EXPIRY', label: 'Near expiry',        hint: 'saleable',   pill: 'lime' },
  { value: 'DAMAGED',     label: 'Damaged / leaked',   hint: 'quarantine', pill: 'orange' },
  { value: 'DEFECTIVE',   label: 'Defective / faulty', hint: 'quarantine', pill: 'gold' },
  { value: 'MISMATCH',    label: 'Batch mismatch',     hint: 'quarantine', pill: 'gold' },
  { value: 'EXPIRED',     label: 'Expired',            hint: 'quarantine', pill: 'red' },
  { value: 'BLOCKED',     label: 'Hold / recall',      hint: 'blocked',    pill: 'red' },
] as const;

type Condition = (typeof CONDITION_OPTIONS)[number]['value'];
type Alloc = { qty: number; batchNo: string; condition: Condition };

// The batch number physically received. Suggests the lot(s) DISPATCHED for this line (the cross-check) AND the
// product's other lots at this warehouse — because the dispatch batch may have been mis-recorded, or the retailer
// may have sent a different lot. Free entry too (a lot the system has never seen). Anything other than a
// dispatched lot flags a mismatch so it gets investigated (usually paired with the "Batch mismatch" condition).
function ReceivedBatchInput({ warehouseId, productId, dispatched, claimed, value, onChange }: {
  warehouseId: string; productId: string; dispatched: any[]; claimed?: string | null; value: string; onChange: (v: string) => void;
}) {
  const { data: whBatches } = useQuery({
    queryKey: ['batches', String(warehouseId), String(productId)],
    queryFn: () => request<any[]>({ url: '/batches', params: { warehouseId, productId } }),
    enabled: !!warehouseId && !!productId,
  });
  const dispNos = new Set((dispatched ?? []).map((d: any) => String(d.batchNo)));
  const exp = (e: any) => (e ? ` · exp ${String(e).slice(0, 10)}` : '');
  const dispatchedOpts = (dispatched ?? []).map((d: any) => ({
    value: String(d.batchNo),
    label: <span>{d.batchNo}<span className="text-subtle">{exp(d.expDate)} · {fmtQty(d.qty)} dispatched</span></span>,
  }));
  // Other lots of this product on hand at the warehouse that were NOT dispatched on this order.
  const otherOpts = (whBatches ?? [])
    .filter((b: any) => !dispNos.has(String(b.batchNo)))
    .map((b: any) => ({
      value: String(b.batchNo),
      label: <span>{b.batchNo}<span className="text-subtle">{exp(b.expDate)} · {fmtQty(b.availableQty)} on hand</span></span>,
    }));
  const options: any[] = [];
  if (dispatchedOpts.length) options.push({ label: 'Dispatched on this order', options: dispatchedOpts });
  if (otherOpts.length) options.push({ label: 'Other lots of this product', options: otherOpts });
  const typed = value.trim();
  const claimedNo = (claimed ?? '').trim();
  /*
   * WHAT A MISMATCH IS MEASURED AGAINST.
   *
   * The raiser states the lot they are sending back, so that claim — the most specific, most recent statement
   * about these physical goods — is what the lot in the WHM's hand is checked against. Falling back to the
   * dispatch record only when no claim was made keeps every older return behaving exactly as it did.
   */
  const vsClaim = !!claimedNo && !!typed && typed.toLowerCase() !== claimedNo.toLowerCase();
  const vsDispatch = !claimedNo && dispNos.size > 0 && !!typed && !dispNos.has(typed);
  return (
    <div className="flex-1" style={{ minWidth: 0 }}>
      <AutoComplete
        style={{ width: '100%' }}
        options={options}
        value={value}
        onChange={onChange}
        allowClear
        placeholder={dispatched?.length ? 'Confirm / enter lot' : 'Enter lot no'}
        filterOption={(input, opt) => String(opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
      />
      {vsClaim && <div className="mt-1 type-label text-warning-600">⚠ not the lot claimed ({claimedNo}) — investigate / mark “Batch mismatch”</div>}
      {vsDispatch && <div className="mt-1 type-label text-warning-600">⚠ not the dispatched lot — investigate / mark “Batch mismatch”</div>}
    </div>
  );
}

export function ReceiveReturnModal({ open, onClose, returnId, warehouseId, lines }: {
  open: boolean; onClose: () => void; returnId: string; warehouseId: string; lines: any[];
}) {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Record<string, Alloc[]>>({});
  const [notes, setNotes] = useState('');

  // Seed each line to a single full-receipt row (received = requested, resalable). The batch pre-fills from the
  // lot the raiser CLAIMED they were sending — the most specific statement anyone has made about these goods —
  // falling back to the dispatched lot when there is exactly one and no claim was made (every return raised
  // before the claim existed). The WHM confirms or corrects it against what is physically in front of them.
  useEffect(() => {
    if (!open) return;
    const seed: Record<string, Alloc[]> = {};
    for (const l of lines ?? []) {
      const disp = l.dispatchedBatches ?? [];
      const claimed = String(l.claimedBatchNo ?? '').trim();
      seed[String(l.id)] = [{
        qty: Number(l.requestedQty ?? l.qty ?? 0),
        batchNo: claimed || (disp.length === 1 ? String(disp[0].batchNo) : ''),
        condition: 'RESALABLE',
      }];
    }
    setRows(seed);
    setNotes('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAllocs = (id: string, fn: (a: Alloc[]) => Alloc[]) => setRows((m) => ({ ...m, [id]: fn(m[id] ?? []) }));
  const updateRow = (id: string, i: number, patch: Partial<Alloc>) => setAllocs(id, (a) => a.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addRow = (id: string, disp: any[]) => setAllocs(id, (a) => [...a, { qty: 0, batchNo: disp?.length === 1 ? String(disp[0].batchNo) : '', condition: 'RESALABLE' }]);
  const removeRow = (id: string, i: number) => setAllocs(id, (a) => a.filter((_, j) => j !== i));

  const lineTotal = (l: any) => (rows[String(l.id)] ?? []).reduce((s, a) => s + Number(a.qty || 0), 0);
  const totalReceived = (lines ?? []).reduce((s, l) => s + lineTotal(l), 0);
  const overCap = (lines ?? []).some((l: any) => lineTotal(l) > Number(l.requestedQty ?? l.qty ?? 0) + 1e-6);

  const send = useConfirmedMutation({ title: "Receive these returned goods?", content: "Sellable stock goes back into the warehouse. A credit note is raised separately.", okText: "Yes, receive" }, {
    mutationFn: () => request({
      url: `/returns/${returnId}/receive`, method: 'POST',
      data: {
        notes: notes.trim() || undefined,
        lines: (lines ?? []).map((l: any) => ({
          returnLineId: String(l.id),
          allocations: (rows[String(l.id)] ?? [])
            .filter((a) => Number(a.qty) > 0)
            .map((a) => ({ qty: Number(a.qty), condition: a.condition, batchNo: a.batchNo?.trim() || undefined })),
        })),
      },
    }),
    onSuccess: (r: any) => {
      message.success(`Return ${r?.returnNo ?? ''} received — stock updated`);
      qc.invalidateQueries({ queryKey: ['return', String(returnId)] });
      qc.invalidateQueries({ queryKey: ['returns'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      qc.invalidateQueries({ queryKey: ['stock'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Receive failed'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Receive returned goods"
      width={820}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={send.isPending} disabled={totalReceived <= 0 || overCap} onClick={() => send.mutate()}>
            {totalReceived > 0 ? `Receive ${fmtQty(totalReceived)} unit${totalReceived === 1 ? '' : 's'}` : 'Receive'}
          </Button>
        </>
      }
    >
      <div className="mb-4 rounded-control border border-sky-500/25 bg-sky-500/10 px-3.5 py-2.5 text-sm leading-relaxed text-content">
        Confirm what arrived, the batch it came in, and its condition. A line can split across batches/conditions —
        add a row for each. <span className="text-muted">Saleable (good / near-expiry) re-enters sellable stock;
        damaged, batch-mismatch, expired and hold/recall are quarantined. Short receipts are fine.</span>
      </div>

      {(lines ?? []).map((l: any) => {
        const id = String(l.id);
        const allocs = rows[id] ?? [];
        const requested = Number(l.requestedQty ?? l.qty ?? 0);
        const got = lineTotal(l);
        const over = got > requested + 1e-6;
        const disp = l.dispatchedBatches ?? [];
        const pillColor = over ? 'red' : got <= 0 ? 'default' : got >= requested - 1e-6 ? 'green' : 'gold';
        // Rows sharing the same lot + condition are fungible and get merged into one on save — flag it so the WHM knows.
        const activeKeys = allocs.filter((a) => Number(a.qty) > 0).map((a) => `${a.condition}|${a.batchNo.trim()}`);
        const hasDupes = new Set(activeKeys).size < activeKeys.length;
        // Reconcile vs dispatch: warn (don't block) when more of a DISPATCHED lot is entered than was shipped on
        // this order — likely a bad split, OR a mis-recorded dispatch batch. The WHM decides; nothing is blocked.
        const dispByBatch = new Map<string, number>((disp ?? []).map((d: any) => [String(d.batchNo), Number(d.qty || 0)]));
        const enteredByBatch = new Map<string, number>();
        for (const a of allocs) { const bn = a.batchNo.trim(); if (Number(a.qty) > 0 && bn) enteredByBatch.set(bn, (enteredByBatch.get(bn) ?? 0) + Number(a.qty)); }
        const overDispatched = [...enteredByBatch.entries()].filter(([bn, qy]) => dispByBatch.has(bn) && qy > (dispByBatch.get(bn) ?? 0) + 1e-6);
        return (
          <div key={id} className="mb-3 overflow-hidden rounded-control border border-line">
            <div className="flex items-start justify-between gap-2 border-b border-line bg-surface-2 px-3.5 py-2">
              <div className="min-w-0">
                <span className="font-medium text-content">{l.productName || `#${l.productId}`}</span>
                {/* WHAT WAS CLAIMED ABOUT THIS PRODUCT. The WHM is checking goods against an assertion, and
                    until now the assertion was not on the screen they check it on — the lot and the fault
                    both lived a page away. Inspecting is a comparison; it needs both sides. */}
                {(l.claimedBatchNo || l.reasonNote) && (
                  <div className="type-caption text-muted">
                    {l.claimedBatchNo && <>claimed lot <span className="font-medium text-content">{l.claimedBatchNo}</span></>}
                    {l.claimedBatchNo && l.reasonNote ? ' · ' : ''}
                    {l.reasonNote}
                  </div>
                )}
              </div>
              <Pill color={pillColor}>received {fmtQty(got)} / {fmtQty(requested)}{over ? ' · over' : ''}</Pill>
            </div>

            <div className="px-3.5 py-3">
              <div className="mb-1.5 flex gap-2 type-overline text-subtle">
                <span style={{ width: 80 }}>Qty</span>
                <span className="flex-1">Batch received</span>
                <span style={{ width: 260 }}>Condition</span>
                <span style={{ width: 28 }} />
              </div>

              {allocs.map((a, i) => (
                <div key={i} className="mb-1.5 flex items-start gap-2">
                  <InputNumber
                    min={0}
                    max={requested}
                    step={1}
                    value={a.qty}
                    status={got > requested ? 'error' : undefined}
                    onChange={(v) => updateRow(id, i, { qty: Number(v ?? 0) })}
                    style={{ width: 80 }}
                  />
                  <ReceivedBatchInput warehouseId={warehouseId} productId={String(l.productId)} dispatched={disp} claimed={l.claimedBatchNo} value={a.batchNo} onChange={(v) => updateRow(id, i, { batchNo: v })} />
                  <Select
                    style={{ width: 260 }}
                    value={a.condition}
                    onChange={(v) => updateRow(id, i, { condition: v })}
                    // Rich label (name + disposition Pill) shows in both the closed box and the dropdown.
                    options={CONDITION_OPTIONS.map((o) => ({
                      value: o.value,
                      label: <span className="inline-flex items-center gap-2">{o.label}<Pill color={o.pill}>{o.hint}</Pill></span>,
                    }))}
                  />
                  <button
                    type="button"
                    className="flex h-8 w-7 items-center justify-center rounded-control text-subtle transition-colors hover:bg-surface-2 hover:text-danger-600 disabled:pointer-events-none disabled:opacity-30"
                    disabled={allocs.length <= 1}
                    onClick={() => removeRow(id, i)}
                    title="Remove this sub-lot"
                  >✕</button>
                </div>
              ))}

              <div className="mt-1 flex items-center gap-3">
                <button
                  type="button"
                  className="type-body-medium text-brand-700 transition-colors hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300"
                  onClick={() => addRow(id, disp)}
                >+ Add batch / condition</button>
                {hasDupes && <span className="text-xs text-subtle">Same lot + condition rows are combined on save.</span>}
              </div>
              {overDispatched.map(([bn, qy]) => (
                <div key={bn} className="mt-1.5 type-label text-warning-600">
                  ⚠ {fmtQty(qy)} entered for {bn} — only {fmtQty(dispByBatch.get(bn))} was dispatched on this order. Re-check the split (or the dispatch entry).
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <p className="mt-1 text-xs leading-relaxed text-subtle">
        <span className="font-medium text-muted">Batch received</span> is pre-filled with the dispatched lot — change it if the physical lot differs (a mismatch is flagged).
        <span className="font-medium text-muted"> Condition</span> decides where each sub-lot goes: saleable re-enters that lot; quarantine / blocked can’t be sold. Split a product with <span className="font-medium text-brand-700 dark:text-brand-400">+ Add batch / condition</span>.
      </p>

      <div className="mt-4">
        <div className="mb-1.5 type-label text-muted">Receipt note (optional)</div>
        <Input.TextArea rows={2} maxLength={255} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the accounts team should know (mismatch, partial, packaging…)" />
      </div>
    </Modal>
  );
}
